import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormDatePicker } from '../../../components/Form/FormDatePicker';
import { StatusBadge } from '../../../hooks/StatusBatch';
import { useCasePermissions } from '../../../hooks/UseCasePermission';

import type { User } from '../../../types/user';
import { useTableFilters } from '../../../hooks/UseTableFilter';
import { TablePagination } from '../../../components/common/TablePagination';
import { formatDateToMyanmar } from '../../../utils/GlobalUtils';
import { deleteLegalAdviceForm, fetchLegalAdviceApplications } from '../../../services/LegalAdviceEightService';
import type { LegalAdviceFormEightData } from '../../../types/LegalAdvices/LegalAdviceFormEight.type';

export function LegalFormEightTable() {
  const navigate = useNavigate();
  const itemsPerPage = 10;

  const filters = useTableFilters();

  const [currentUser] = useState<User | null>(() => {
    if (typeof window === 'undefined') return null;
    try {
      return JSON.parse(localStorage.getItem('user') || 'null');
    } catch {
      return null;
    }
  });

  useCasePermissions(currentUser);

  const { data, isLoading, isError, error, isPlaceholderData, refetch, isFetching } = useQuery({
    queryKey: [
      'legalFormEight',
      filters.debouncedSearch,
      filters.debouncedCourt,
      filters.debouncedParty,
      filters.selectedStatus,
      filters.selectedDate,
      filters.currentPage,
    ],
    queryFn: () =>
      fetchLegalAdviceApplications({
        searchTerm: filters.debouncedSearch.trim() || undefined,
        courtSearch: filters.debouncedCourt.trim() || undefined,
        partySearch: filters.debouncedParty.trim() || undefined,
        selectedStatus: filters.selectedStatus || undefined,
        selectedDate: filters.selectedDate || undefined,
        currentPage: filters.currentPage,
        itemsPerPage,
      }),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const rawApplications: LegalAdviceFormEightData[] = data?.data || [];

  const checkIsOriginUser = (app: LegalAdviceFormEightData): boolean => {
    if (!currentUser) return false;

    // 1. Match by Office ID
    const originOfficeId = app.originOffice?.id;
    if (currentUser.officeId && originOfficeId && currentUser.officeId === originOfficeId) {
      return true;
    }

    // 2. Match by Creator User ID
    const createdById = app.createdBy?.id;
    if (currentUser.id && createdById && currentUser.id === createdById) {
      return true;
    }

    return false;
  };

  const applications = rawApplications.filter((app) => {
    const isOriginUser = checkIsOriginUser(app);

    if (filters.selectedStatus === 'SUBMITTED') {
      return isOriginUser;
    }
    if (filters.selectedStatus === 'RECEIVED') {
      return !isOriginUser;
    }
    return true;
  });

  const totalItems = data?.totalItems || 0;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const handleDelete = async (id?: string) => {
    if (!id) return;
    const confirmed = window.confirm('ဤမှတ်တမ်းကို ဖျက်ရန် သေချာပါသလား။');
    if (!confirmed) return;

    try {
      await deleteLegalAdviceForm(id);
      await refetch();
    } catch (err: unknown) {
      console.error('Delete failed:', err);
      const errorMessage = err instanceof Error ? err.message : 'ဖျက်ခြင်း မအောင်မြင်ပါ။';
      alert(errorMessage);
    }
  };

  return (
    <div className="space-y-4 bg-white p-4 md:p-6 rounded-2xl border border-slate-200 shadow-sm">
      {/* Header & Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900 tracking-tight">
            ဥပရအမှုလိုက်ပုံစံ (၈) စာရင်းများ
          </h2>
        </div>

        <div className="flex items-center gap-2 self-start sm:self-center shrink-0">
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50 transition-colors disabled:opacity-50"
            title="ဒေတာများ နောက်ဆုံး Data များ ရယူရန်"
          >
            <svg
              className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin text-blue-600' : 'text-slate-500'}`}
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"
              />
            </svg>
            နောက်ဆုံး Data များ ရယူရန်
          </button>

          <button
            type="button"
            onClick={() => navigate('/legal-form-eight/create')}
            className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-blue-600 px-3.5 py-2 text-xs font-semibold text-white shadow-sm hover:bg-blue-500 transition-colors shrink-0"
          >
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
            ဖောင်အသစ်ထည့်ရန်
          </button>
        </div>
      </div>

      {/* Filter Section */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 items-end bg-slate-50/60 p-3 rounded-xl border border-slate-100">
        <div className="lg:col-span-2">
          <label className="block text-[11px] font-medium text-slate-500 mb-1">အထွေထွေ ရှာဖွေရန်</label>
          <input
            type="text"
            placeholder="စိစစ်သည့်အမှုအမှတ်/တရားရုံး/တရားလို/တရားခံ/စာအမှတ်..."
            value={filters.searchTerm}
            onChange={(e) => {
              filters.setSearchTerm(e.target.value);
              filters.setCurrentPage(1);
            }}
            className="w-full rounded-md border border-slate-200 bg-white py-1.5 px-2.5 text-xs text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-[11px] font-medium text-slate-500 mb-1">အခြေအနေ</label>
          <select
            value={filters.selectedStatus}
            onChange={(e) => {
              filters.setSelectedStatus(e.target.value);
              filters.setCurrentPage(1);
            }}
            className="w-full rounded-md border border-slate-200 bg-white py-1.5 px-2.5 text-xs text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="">အခြေအနေ အားလုံး</option>
            <option value="DRAFT">မူကြမ်း</option>
            <option value="APPROVED">အတည်ပြုပြီး</option>
            <option value="SUBMITTED">တင်ပြပြီး</option>
            <option value="RECEIVED">လက်ခံရရှိသည်</option>
            <option value="UNDER_REVIEW">ကြည့်ရှုထားသည်</option>
            <option value="ATTACHED">ညွန်ကြားထားသည်</option>
          </select>
        </div>

        <div className="flex items-center gap-1.5">
          <div className="flex-1">
            <label className="block text-[11px] font-medium text-slate-500 mb-1">ရက်စွဲ</label>
            <FormDatePicker
              value={filters.selectedDate}
              onChange={(e) => {
                filters.setSelectedDate(e.target.value);
                filters.setCurrentPage(1);
              }}
              name="selectedDate"
              className="w-full"
            />
          </div>

          {filters.hasActiveFilters && (
            <button
              type="button"
              onClick={filters.handleClearFilters}
              title="စစ်ထုတ်မှုများ ဖျက်ရန်"
              className="mt-5 shrink-0 inline-flex items-center justify-center text-xs font-medium text-rose-600 hover:bg-rose-50 border border-rose-200 rounded-md py-1.5 px-2 bg-white transition-colors"
            >
              🔄
            </button>
          )}
        </div>
      </div>

      {/* Table Section */}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm relative">
        {isPlaceholderData && (
          <div className="absolute inset-0 bg-white/40 backdrop-blur-[1px] flex items-center justify-center z-10">
            <span className="text-xs bg-slate-800 text-white px-2.5 py-1 rounded-full shadow-sm">
              နောက်ဆုံးပေါ်အချက်အလက် ရယူနေပါသည်...
            </span>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full min-w-full table-auto divide-y divide-slate-200 text-left text-sm text-slate-600">
            <thead className="bg-slate-50 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-3.5 py-3 w-[140px]">စာရွက်စာတမ်း</th>
                <th className="px-3.5 py-3 w-[160px]">တရားရုံး / ပေးပို့သည့်ရုံး</th>
                <th className="px-3.5 py-3 w-[140px]">စွဲဆိုသည့်ပုဒ်မ</th>
                <th className="px-3.5 py-3 min-w-[180px]">လျှောက်လဲချက် သုံးသပ်ချက်</th>
                <th className="px-3.5 py-3 w-[110px]">တင်ပြသည့် (သို့) ဖန်တီး သည့် ရက်စွဲ</th>
                <th className="px-3.5 py-3 w-[120px]">အခြေအနေ</th>
                <th className="px-3.5 py-3 w-[110px] text-right">လုပ်ဆောင်ချက်</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 bg-white text-slate-900">
              {isLoading && !isPlaceholderData ? (
                <tr>
                  <td colSpan={7} className="text-center py-12 text-xs text-slate-400">
                    အချက်အလက်များ ဆွဲယူနေပါသည်...
                  </td>
                </tr>
              ) : isError ? (
                <tr>
                  <td colSpan={7} className="text-center py-12 text-xs text-red-500">
                    {error instanceof Error ? error.message : 'အချက်အလက်များ ဆွဲယူရာတွင် အမှားအယွင်းရှိခဲ့ပါသည်။'}
                  </td>
                </tr>
              ) : applications.length > 0 ? (
                applications.map((app) => {
                  const status = app.status || 'DRAFT';
                  const isOriginUser = checkIsOriginUser(app);
                  const caseNumberDisplay = app.scrutinyCaseNo || app.scrutinyRegisterNo || '-';
                  const primaryLawSection = app.prosecutedLawSection || app.chargedLawSection || '-';

                  return (
                    <tr key={app.id} className="hover:bg-slate-50/75 transition-colors">
                      {/* Case No. & Document No. */}
                      <td className="whitespace-nowrap px-3.5 py-3 text-xs">
                        <div className="font-semibold text-blue-600">{app.documentNumber || caseNumberDisplay}</div>
                        {app.documentNumber && (
                          <div className="text-[10px] text-slate-400 font-normal">{caseNumberDisplay}</div>
                        )}
                      </td>

                      {/* Court & Submitter Details */}
                      <td className="px-3.5 py-3 text-slate-700 text-xs">
                        <div className="font-medium">{app.acquittalCourtDetails || '-'}</div>
                        <div
                          className="text-[10px] text-slate-400 truncate max-w-[150px]"
                          title={app.submitterNameOrOffice}
                        >
                          {app.submitterNameOrOffice || '-'}
                        </div>
                      </td>

                      {/* Charged / Prosecuted Section */}
                      <td className="px-3.5 py-3 text-slate-700 text-xs">
                        <span className="inline-flex items-center rounded-md bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-800">
                          {primaryLawSection}
                        </span>
                      </td>

                      {/* Plaintiff & Defendant Arguments Analysis */}
                      <td className="px-3.5 py-3 text-slate-600 text-xs">
                        <div className="line-clamp-1 font-medium text-slate-800" title={app.plaintiffArgumentAnalysis}>
                          တရားလို - {app.plaintiffArgumentAnalysis || '-'}
                        </div>
                        <div className="line-clamp-1 text-slate-500 text-[11px]" title={app.defendantArgumentAnalysis}>
                          တရားခံ - {app.defendantArgumentAnalysis || '-'}
                        </div>
                      </td>

                      {/* Submission Date */}
                      <td className="whitespace-nowrap px-3.5 py-3 text-slate-500 text-xs">
                        {formatDateToMyanmar(app.submitDate || app.createdAt) || '-'}
                      </td>

                      {/* Status Badge */}
                      <td className="whitespace-nowrap px-3.5 py-3 text-xs">
                        <StatusBadge status={status} isOriginUser={isOriginUser} />
                      </td>

                      {/* Actions */}
                      <td className="whitespace-nowrap px-3.5 py-3 text-right text-xs">
                        <div className="flex justify-end gap-1 items-center">
                          {status === 'DRAFT' ? (
                            <>
                              <button
                                type="button"
                                onClick={() => navigate(`/legal-form-eight/edit/${app.documentNumber}`)}
                                className="inline-flex items-center gap-1 rounded bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-600 hover:bg-blue-100 transition-colors"
                                title="ပြင်ဆင်ရန်"
                              >
                                ✏️ ပြင်ဆင်ရန်
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDelete(app.documentNumber)}
                                className="rounded p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-600 transition-colors"
                                title="ဖျက်ရန်"
                              >
                                🗑️
                              </button>
                            </>
                          ) : status === 'APPROVED' || (status === 'SUBMITTED' && isOriginUser) || (status === 'UNDER_REVIEW' && isOriginUser) ? (
                            <button
                              type="button"
                              onClick={() => navigate(`/legal-form-eight/edit/${app.documentNumber}`)}
                              className="inline-flex items-center gap-1 rounded bg-blue-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-blue-700 transition-colors shadow-sm"
                              title="ပေးပို့ရန်"
                            >
                              🚀 တင်ပြရန်
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => navigate(`/legal-form-eight-detail/${app.documentNumber}`)}
                              className="rounded px-2.5 py-1 text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-700 transition-colors shadow-sm"
                            >
                              🔍 စိစစ်ရန်
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={7} className="text-center py-12 text-xs text-slate-400">
                    ကိုက်ညီသော အချက်အလက် ရှာမတွေ့ပါ။
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {!isLoading && totalPages > 1 && (
          <TablePagination
            currentPage={filters.currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={filters.setCurrentPage}
          />
        )}
      </div>
    </div>
  );
}